import { useEffect } from 'react';
import { useAppDispatch } from '../../../app/hooks';
import { setNumAPIItems } from '../apiExplorerSlice';

// TODO: There is a likely a much better way to do this using the DataGrid API.  It's taking too long to get it working right now, but this should be replaced with the correct method later.  Here are some links to the docs and starting points of code to look at:
// https://mui.com/x/react-data-grid/pagination/#selectors
// https://mui.com/x/react-data-grid/filtering/#selectors

// const valueGridPaginationRowCountSelector =
//   gridPaginationRowCountSelector(apiRef);
// const valueGridFilteredRowCountSelector =
//   gridFilteredRowCountSelector(apiRef);
// const valueGridExpandedRowCountSelector =
//   gridExpandedRowCountSelector(apiRef);
// const valueGridFilteredTopLevelRowCountSelector =
//   gridFilteredTopLevelRowCountSelector(apiRef);

// console.table({
//   'Pagination Row Count': valueGridPaginationRowCountSelector,
//   'Filtered Row Count': valueGridFilteredRowCountSelector,
//   'Expanded Row Count': valueGridExpandedRowCountSelector,
//   'Filtered Top Level Row Count': valueGridFilteredTopLevelRowCountSelector,
// });

/**
 * useTrackCounts
 *
 * This hook encapsulates the logic to track the total and filtered counts of items in the DataGrid.
 * It supports two ways of filtering the grid: via the search bar and via individual column filters,
 * which can both be active at the same time. The hook uses a MutationObserver to monitor DOM changes
 * (e.g. pagination text) that reflect the state of column filters, and it also updates counts when
 * the search filter changes.
 *
 * The hacky method is necessary because the DataGrid does not provide direct hooks or events
 * to track filtered count updates when using column filters. Thus, by observing mutations in specific
 * DOM elements, we are able to detect changes to the displayed pagination information and update
 * the state accordingly.
 *
 * @param loading - whether the grid is in loading state.
 * @param total - total number of items (i.e. data.length).
 * @param searchFilteredRowsLength - number of rows after applying the search filter.
 */
export const useTrackCounts = (
  loading: boolean,
  total: number,
  searchFilteredRowsLength: number
) => {
  const dispatch = useAppDispatch();

  // Use MutationObserver to update filtered count from pagination details.
  useEffect(() => {
    if (!loading && total > 0) {
      const observer = new MutationObserver(() => {
        // Use a delay to ensure the DOM updates (pagination text) have occurred.
        setTimeout(() => {
          const paginationElements = document.querySelectorAll(
            '.MuiTablePagination-displayedRows'
          );

          if (paginationElements && paginationElements.length > 0) {
            const paginationText = paginationElements[0].textContent;
            if (paginationText) {
              const match = paginationText.match(/of\s+(\d+)/i);
              if (match && match[1]) {
                const filteredCount = parseInt(match[1], 10);
                dispatch(
                  setNumAPIItems({
                    filtered: filteredCount,
                    total: total,
                  })
                );
              }
            }
          } else {
            // Fallback: if pagination is not visible, count rows via DOM
            const gridElement = document.querySelector('.MuiDataGrid-root');
            if (gridElement) {
              const allRows = gridElement.querySelectorAll('[data-id]');
              const filteredCount = allRows.length;
              dispatch(
                setNumAPIItems({
                  filtered: filteredCount,
                  total: total,
                })
              );
            }
          }
        }, 100);
      });

      const gridElement = document.querySelector('.MuiDataGrid-main');
      if (gridElement) {
        observer.observe(gridElement, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        // Also observe the footer where pagination info is rendered.
        const footerElement = document.querySelector(
          '.MuiDataGrid-footerContainer'
        );
        if (footerElement) {
          observer.observe(footerElement, {
            childList: true,
            subtree: true,
            attributes: true,
          });
        }
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [loading, total, dispatch]);

  // Update filtered count when the search filter changes.
  useEffect(() => {
    if (!loading && total > 0) {
      dispatch(
        setNumAPIItems({
          filtered: searchFilteredRowsLength,
          total: total,
        })
      );
    } else {
      dispatch(setNumAPIItems({ filtered: 0, total: 0 }));
    }
  }, [searchFilteredRowsLength, total, loading, dispatch]);
};
